import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning' | 'info';
    loading?: boolean;
    /** Conteúdo extra abaixo da mensagem (ex.: campo de motivo). Opcional — não afeta quem não passa. */
    children?: React.ReactNode;
    /** Desabilita o botão de confirmar além do `loading` (ex.: campo obrigatório vazio). */
    confirmDisabled?: boolean;
    /** 'largo' para conteúdo extra grande (ex.: pular cálculo com usinas a conferir). */
    largura?: 'padrao' | 'largo';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirmação',
    message,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    variant = 'danger',
    loading = false,
    children,
    confirmDisabled = false,
    largura = 'padrao'
}) => {
    const tituloId = React.useId();

    if (!isOpen) return null;

    const variantStyles = {
        danger: {
            icon: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
            button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        },
        warning: {
            icon: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
            button: 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500',
        },
        info: {
            icon: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
            button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        },
    };

    const styles = variantStyles[variant];

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            {/* role/aria-modal sao aditivos: nenhum modal do Financeiro os tinha, e sem
                eles o leitor de tela nao anuncia que o conteudo de tras ficou inerte. */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={tituloId}
                // Altura presa à tela e só o corpo rola: conteúdo extra grande não esconde os botões.
                className={`bg-white dark:bg-slate-900 rounded-2xl shadow-2xl ${largura === 'largo' ? 'max-w-2xl' : 'max-w-md'} w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-scale-in`}
            >
                {/* Header */}
                <div className="shrink-0 flex items-center justify-between px-6 pt-5 pb-0">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${styles.icon}`}>
                            <AlertTriangle size={20} />
                        </div>
                        <h3 id={tituloId} className="text-lg font-bold text-gray-900 dark:text-slate-100">{title}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Fechar"
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                    <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">{message}</p>
                    {children && <div className="mt-3">{children}</div>}
                </div>

                {/* Footer */}
                <div className="shrink-0 flex justify-end gap-3 px-6 pb-5">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading || confirmDisabled}
                        className={`px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-colors shadow-sm disabled:opacity-50 focus:ring-2 focus:ring-offset-2 ${styles.button}`}
                    >
                        {loading ? 'Processando...' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmModal;
